import React from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import {
  MyTimeCloseActionSection,
  MyTimeCloseConfirmationDialog,
  MyTimeOpenRequestsTable,
  MyTimePageHeader,
  MyTimeWithoutExaminerTable,
} from "my-time/components";
import { useMyTimeClosingPage } from "my-time/hooks/use-closing-page";
import { OcCard, OcPageNameTitle, OcIconForState } from "oc/components";
import { OcLoading } from "oc/components";

export const MyTimeClosingCurrentPeriodPage = () => {
  const { t } = useTranslation();
  const {
    date,
    formattedDate,
    isMobile,
    showDialog,
    handleDateChange,
    handleCloseClick,
    handleConfirmClose,
    handleCancelClose,
    openRequestsProps,
    withoutExaminerProps,
    openApprovalProps,
    openApprovalWoExaminerProps,
    notAuthorizedProps,
    notAuthorizedWoExaminerProps,
    disabled,
  } = useMyTimeClosingPage();

  const {
    selectedRowId: openRequestsRawSelectedRowId,
    ...openRequestsBaseProps
  } = openRequestsProps;
  const openRequestsSelectedRowId = openRequestsRawSelectedRowId ?? undefined;

  const {
    selectedRowId: openApprovalRawSelectedRowId,
    ...openApprovalBaseProps
  } = openApprovalProps;
  const openApprovalSelectedRowId = openApprovalRawSelectedRowId ?? undefined;

  const {
    selectedRowId: notAuthorizedRawSelectedRowId,
    ...notAuthorizedBaseProps
  } = notAuthorizedProps;
  const notAuthorizedSelectedRowId = notAuthorizedRawSelectedRowId ?? undefined;

  const noActiveRequests = [
    openRequestsProps,
    withoutExaminerProps,
    openApprovalProps,
    openApprovalWoExaminerProps,
    notAuthorizedProps,
    notAuthorizedWoExaminerProps,
  ].every(
    (props) =>
      !props.loading &&
      Array.isArray(props.data.rows) &&
      props.data.rows.length === 0 &&
      !props.error
  );

  const isAnyLoading =
    openRequestsProps.loading ||
    withoutExaminerProps.loading ||
    openApprovalProps.loading ||
    openApprovalWoExaminerProps.loading ||
    notAuthorizedProps.loading ||
    notAuthorizedWoExaminerProps.loading;

  return (
    <Box>
      <OcLoading show={isAnyLoading} />
      <OcPageNameTitle title={t("MY_TIME_CLOSE_MONTH")} goBack={false} />
      <Box padding="0px 16px">
        <MyTimePageHeader
          formattedDate={formattedDate}
          date={date}
          onDateChange={handleDateChange}
          isMobile={isMobile}
          closingperiodPage
        />
      </Box>
      <MyTimeCloseConfirmationDialog
        open={showDialog}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
      <Box mt={2} px={isMobile ? 0 : 2}>
        {noActiveRequests && (
          <>
            <Box height="52px" />
            <OcIconForState
              data={openRequestsProps.data.rows}
              loading={openRequestsProps.loading}
              error={openRequestsProps.error}
              isMobile={isMobile}
              module="MyTime"
            />
          </>
        )}
        {!noActiveRequests && (
          <>
            {openRequestsProps.data.rows.length > 0 && (
              <OcCard
                label={t("MY_TIME_ACTIVE_REQUESTS")}
                loading={openRequestsProps.loading}
                error={!!openRequestsProps.error}
              >
                <MyTimeOpenRequestsTable
                  {...openRequestsBaseProps}
                  selectedRowId={openRequestsSelectedRowId}
                  data={openRequestsProps.data.rows}
                />
              </OcCard>
            )}
            {withoutExaminerProps.data.rows.length > 0 && (
              <Box mt={2}>
                <OcCard
                  label={t("MY_TIME_UNEXAMINED_REQUESTS")}
                  loading={withoutExaminerProps.loading}
                  error={!!withoutExaminerProps.error}
                >
                  <MyTimeWithoutExaminerTable
                    {...withoutExaminerProps}
                    data={withoutExaminerProps.data.rows}
                  />
                </OcCard>
              </Box>
            )}
            {openApprovalProps.data.rows.length > 0 && (
              <Box mt={2}>
                <OcCard
                  label={t("MY_TIME_NOT_APPROVED_TIMESHEETS")}
                  loading={openApprovalProps.loading}
                  error={!!openApprovalProps.error}
                >
                  <MyTimeOpenRequestsTable
                    {...openApprovalBaseProps}
                    selectedRowId={openApprovalSelectedRowId}
                    data={openApprovalProps.data.rows}
                  />
                </OcCard>
              </Box>
            )}
            {openApprovalWoExaminerProps.data.rows.length > 0 && (
              <Box mt={2}>
                <OcCard
                  label={t("MY_TIME_NOT_APPROVED_TIMESHEETS_WO_EXAMINER")}
                  loading={openApprovalWoExaminerProps.loading}
                  error={!!openApprovalWoExaminerProps.error}
                >
                  <MyTimeWithoutExaminerTable
                    {...openApprovalWoExaminerProps}
                    data={openApprovalWoExaminerProps.data.rows}
                  />
                </OcCard>
              </Box>
            )}
            {notAuthorizedProps.data.rows.length > 0 && (
              <Box mt={2}>
                <OcCard
                  label={t("MY_TIME_NOT_AUTHORIZED_TIMESHEETS")}
                  loading={notAuthorizedProps.loading}
                  error={!!notAuthorizedProps.error}
                >
                  <MyTimeOpenRequestsTable
                    {...notAuthorizedBaseProps}
                    selectedRowId={notAuthorizedSelectedRowId}
                    data={notAuthorizedProps.data.rows}
                  />
                </OcCard>
              </Box>
            )}
            {notAuthorizedWoExaminerProps.data.rows.length > 0 && (
              <Box mt={2}>
                <OcCard
                  label={t("MY_TIME_NOT_AUTHORIZED_TIMESHEETS_WO_EXAMINER")}
                  loading={notAuthorizedWoExaminerProps.loading}
                  error={!!notAuthorizedWoExaminerProps.error}
                >
                  <MyTimeWithoutExaminerTable
                    {...notAuthorizedWoExaminerProps}
                    data={notAuthorizedWoExaminerProps.data.rows}
                  />
                </OcCard>
              </Box>
            )}
          </>
        )}
        <Box mt={2}>
          <MyTimeCloseActionSection
            disabled={!!disabled}
            onCloseClick={handleCloseClick}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default MyTimeClosingCurrentPeriodPage;
